import React, { useCallback, useEffect, useId, useState } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { sendExperimentEvent } from 'Utils/Experiments'
import formatPrice from 'Utils/formatPrice'

const TRANSLATION_KEY_PREFIX = 'subscription_checkout.plan_selector'
const PER_MODEL_COPY_EXPERIMENT_ID = 'checkout_per_model_copy'

function PlanSelector({ plans, selectedKey, onChange: handleChange, locale = 'en', perModelCopyEnabled = null }) {
  const groupName = useId()
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  // The bucketed value is not applied during SSR prerender, so the server always
  // renders control. Gate the variant behind mount so SSR and the first client
  // render match (no hydration mismatch); the variant swaps in after mount.
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (typeof perModelCopyEnabled !== 'boolean') return
    sendExperimentEvent(PER_MODEL_COPY_EXPERIMENT_ID, perModelCopyEnabled)
  }, [perModelCopyEnabled])

  const badgeClass = (badge) => classnames(
    'plan-selector__badge cgt-overline-sm',
    badge === 'save_60' && 'plan-selector__badge--savings',
    badge === 'cancel_anytime' && 'plan-selector__badge--anytime',
  )

  return (
    <div
      className="plan-selector"
      role="radiogroup"
      aria-label={translate(`${TRANSLATION_KEY_PREFIX}.section_title`)}
    >
      <div className="plan-selector__title cgt-overline-sm">
        {translate(`${TRANSLATION_KEY_PREFIX}.section_title`)}
      </div>

      <div className="plan-selector__row">
        {plans.map((plan) => {
          const isSelected = plan.key === selectedKey
          const planName = translate(`${TRANSLATION_KEY_PREFIX}.names.${plan.key}`)
          const subtitle = translate(`${TRANSLATION_KEY_PREFIX}.subtitles.${plan.subtitle}`)
          const badgeLabel = translate(`${TRANSLATION_KEY_PREFIX}.badges.${plan.badge}`)
          const cardClass = classnames(
            'plan-selector__option',
            isSelected && 'plan-selector__option--selected',
          )

          const inputId = `subscription-plan-${plan.key}`
          const showPerModelCopy = isMounted && perModelCopyEnabled === true && plan.pricePerModel != null
          const displayedPrice = showPerModelCopy ? plan.pricePerModel : plan.price
          const priceUnitKey = showPerModelCopy
            ? `${TRANSLATION_KEY_PREFIX}.price_unit_per_model`
            : 'subscription_checkout.order_summary.price_unit'

          return (
            <label key={plan.key} htmlFor={inputId} className={cardClass}>
              <input
                id={inputId}
                type="radio"
                className="plan-selector__option-input"
                name={groupName}
                value={plan.key}
                checked={isSelected}
                onChange={() => handleChange(plan.key)}
                aria-label={translate(`${TRANSLATION_KEY_PREFIX}.select_aria`, { name: planName })}
              />

              <div className="plan-selector__option-header">
                <span className="plan-selector__option-name cgt-body-base-semibold">
                  {planName}
                </span>
                <span className={badgeClass(plan.badge)}>{badgeLabel}</span>
              </div>

              <span className="plan-selector__option-price-row">
                <span className="plan-selector__current-price cgt-heading-300">{formatPrice(displayedPrice)}</span>
                <span className="plan-selector__period cgt-body-base">
                  {translate(priceUnitKey)}
                </span>
              </span>

              <span className="plan-selector__option-subtitle cgt-caption">
                {subtitle}
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

PlanSelector.propTypes = {
  plans: arrayOf(
    shape({
      key: string.isRequired,
      price: number.isRequired,
      pricePerModel: number,
      badge: string,
      subtitle: string,
    }),
  ).isRequired,
  selectedKey: string.isRequired,
  onChange: func.isRequired,
  locale: string,
  perModelCopyEnabled: bool,
}

export default PlanSelector
